import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { FaBars, FaChevronDown, FaTimes } from "react-icons/fa";

import { DonateButton, ZakatButton } from "../../Buttons/Buttons";
import Head from "./Head";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { fetchAboutData } from "../../features/AboutSlice/AboutSlice";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const { about = [] } = useSelector((state) => state.about || {});
  

  // UI state
  const [isOpen, setIsOpen] = useState(false); // mobile main menu
  const [openSubMenu, setOpenSubMenu] = useState(null); // mobile submenu index
  const [isOpenSearch, setIsOpenSearch] = useState(false);

  // hide on scroll
  const [isScrolling, setIsScrolling] = useState(false);
  const prevScrollYRef = useRef(0);

  // Refs for keyboard handling
  const navRef = useRef(null);

  // Fetch about data (refetch on language change)
  useEffect(() => {
    dispatch(fetchAboutData());
  }, [dispatch, i18n.language]);

  // Build links with useMemo (recomputes only when `about` or `t` changes)
  const links = useMemo(() => {
    const whoWeAre = about.filter((item) => item.key === "who_we_are");
    const whatCanYouDo = about.filter((item) => item.key === "what_can_you_do");

    return [
      { name: t("home"), link: "/" },
      {
        name: t("about"),
        link: "/about",
        subItems: [
          {
            section: t("whoWeAre"),
            links: whoWeAre.map((item) => ({
              id: item.id,
              name: item.value,
              link: `/about/${item.id}`,
              icon: item.icon,
            })),
          },
          {
            section: t("whatCanYouDo"),
            links: whatCanYouDo.map((item) => ({
              id: item.id,
              name: item.value,
              link: `/about/${item.id}`,
              icon: item.icon,
            })),
          },
        ],
      },
      { name: t("doctors"), link: "/doctor" },
      { name: t("projects"), link: "/project" },
      { name: t("branches"), link: "/branch" },
      { name: t("unit"), link: "/unit" },
      { name: t("needZad"), link: "/need" },
    ];
  }, [about, t]);

  // Hide/show header on scroll (use ref to avoid re-render loops)
  useEffect(() => {
    const handleScroll = () => {
      const current = window.scrollY;
      const prev = prevScrollYRef.current;
      // scrolling down and scrolled more than 50px -> hide header
      const scrollingDown = current > prev && current > 50;
      setIsScrolling(scrollingDown);
      prevScrollYRef.current = current;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when window resized to desktop
  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 768) {
        setIsOpen(false);
        setOpenSubMenu(null);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Close mobile menus on route change for nicer UX
  useEffect(() => {
    setIsOpen(false);
    setOpenSubMenu(null);
  }, [location.pathname]);

  // Keyboard support: Escape closes menus
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
        setOpenSubMenu(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Toggle handlers (useCallback for stability)
  const toggleMenu = useCallback(() => {
    setIsOpen((v) => {
      if (v) setOpenSubMenu(null); // closing => reset submenus
      return !v;
    });
  }, []);

  const toggleSubMenu = useCallback((index) => {
    setOpenSubMenu((prev) => (prev === index ? null : index));
  }, []);

  // Helper: get first about id (safe)
  const getFirstAboutId = useCallback(() => {
    const first = about.find((it) => it.key === "who_we_are");
    return first ? first.id : null;
  }, [about]);

  // Helper: desktop about link - if a nested path exists choose it otherwise first id
  const getLink = useCallback(
    (link) => {
      // if link contains a path with id already (like /about/123), return as-is
      try {
        const parts = (link || "").split("/").filter(Boolean); // remove empty
        if (parts.length >= 2 && parts[0] === "about" && parts[1]) return `/${parts.slice(0, 2).join("/")}`;
      } catch (e) {
        // ignore
      }
      const firstId = getFirstAboutId();
      return firstId ? `/about/${firstId}` : "/about";
    },
    [getFirstAboutId]
  );

  // mobile submenu max-height wrapper classes
  const submenuMaxH = "max-h-[calc(100vh-6rem)]"; // adjust 6rem if your header is taller

  return (
    <div
      ref={navRef}
      className={`sticky top-0 z-50 bg-white transition-transform duration-300 ${isScrolling ? "-translate-y-full" : "translate-y-0"
        }`}
    >
      <div className={`transition-transform duration-300 ${isScrolling ? "-translate-y-full" : "translate-y-0"}`}>
        <Head isOpenSearch={isOpenSearch} setIsOpenSearch={setIsOpenSearch} />
      </div>

      {/* Desktop Navigation */}
      <nav className="hidden lg:flex z-10 items-center justify-between px-10 py-4 shadow-md">
        <div className="flex gap-8">
          {links.map((item) => (
            <div key={item.name} className="relative group">
              {item.subItems ? (
                <>
                  <NavLink
                    to={getLink(item.link)}
                    className={({ isActive }) =>
                      `flex items-center gap-1 font-semibold transition-colors ${isActive ? "text-[#07A024]" : "text-gray-600 hover:text-[#07A024]"}`
                    }
                  >
                    <span>{item.name}</span>
                    <FaChevronDown />
                  </NavLink>

                  {/* Mega Dropdown (shows on hover) */}
                  <div
                    className={`absolute top-full ${i18n.language === "ar" || i18n.language === "ckb" ? "right-5" : "left-5"} hidden group-hover:block w-[700px] z-10 bg-white/80 backdrop-filter backdrop-blur-xl shadow-xl rounded-lg overflow-hidden`}
                  >
                    <div className="grid grid-cols-2 divide-x divide-gray-100">
                      {item.subItems.map((section, sIdx) => (
                        <div key={section.section + sIdx} className="p-6">
                          <h3 className={`${i18n.language === "ar" || i18n.language === "ckb" ? "text-lg" : "text-sm"} font-semibold text-gray-900 mb-3 uppercase tracking-wide`}>
                            {section.section}
                          </h3>
                          <div className="space-y-2">
                            {section.links.map((subItem) => (
                              <NavLink key={subItem.id || subItem.link} to={subItem.link} className="block p-3 rounded-lg hover:bg-[#07A024]/10 text-gray-600 hover:text-[#07A024] transition-all">
                                <div className="flex gap-4 items-center">
                                  {subItem.icon && (
                                    <img src={process.env.REACT_APP_IMG_URL + subItem.icon} className="w-6 h-6 object-contain" alt="" />
                                  )}
                                  <p>{subItem.name}</p>
                                </div>
                              </NavLink>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <NavLink
                  key={item.name}
                  to={item.link}
                  className={({ isActive }) => `font-semibold transition-colors ${isActive ? "text-[#07A024]" : "text-gray-600 hover:text-[#07A024]"}`}
                >
                  {item.name}
                </NavLink>
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-3 items-center">
          <DonateButton link={"/t.me/baxsharani_zad"} />
          <ZakatButton link={"/zakat"} />
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className="lg:hidden bg-white shadow-md">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            onClick={toggleMenu}
            aria-label={isOpen ? t("closeMenu") || "Close menu" : t("openMenu") || "Open menu"}
            className="text-2xl text-gray-600 p-2"
            aria-expanded={isOpen}
          >
            {isOpen ? <FaTimes /> : <FaBars />}
          </button>

          <div className="flex gap-3 items-center">
            <DonateButton />
            <div className="hidden md:block">
              <ZakatButton link={"/zakat"} isFull={true} />
            </div>
          </div>
        </div>

        {/* Mobile dropdown area */}
        {/* MOBILE DROPDOWN - replace your current outer block with this */}
        <div
          className={`absolute w-full bg-white shadow-lg transition-all duration-300 ${isOpen ? "max-h-[calc(100vh-4rem)] overflow-auto" : "max-h-0 overflow-hidden"
            }`}
          aria-hidden={!isOpen}
        >
          <div className="py-2">
            {links.map((item, idx) => {
              const key = (item.name || "item") + "-" + idx;
              return (
                <div key={key}>
                  {item.subItems ? (
                    <>
                      <button
                        onClick={() => toggleSubMenu(idx)}
                        aria-controls={`mobile-sub-${idx}`}
                        aria-expanded={openSubMenu === idx}
                        className="w-full px-6 py-4 flex justify-between items-center text-gray-700 hover:bg-gray-50 border-b border-gray-100"
                      >
                        <span>{item.name}</span>
                        <FaChevronDown
                          className={`transition-transform duration-200 ${openSubMenu === idx ? "rotate-180" : "rotate-0"}`}
                        />
                      </button>

                      {/* submenu wrapper: fixed calc max-height + inner scroll area */}
                      <div
                        id={`mobile-sub-${idx}`}
                        className={`overflow-hidden bg-gray-50 transition-[max-height] duration-300 ease-in-out ${openSubMenu === idx ? "max-h-[calc(100vh-8rem)]" : "max-h-0"
                          }`}
                      >
                        {/* inner scrollable content so the full list is reachable */}
                        <div className="pt-2 pb-4 max-h-[calc(100vh-12rem)] overflow-auto">
                          {item.subItems.map((section, sIdx) => (
                            <div className="pl-6" key={`${section.section}-${sIdx}`}>
                              <h3 className="px-2 pt-4 pb-2 text-sm font-semibold text-gray-700">{section.section}</h3>
                              {section.links.map((subItem) => (
                                <NavLink
                                  key={subItem.id || subItem.link}
                                  to={subItem.link}
                                  onClick={() => {
                                    setIsOpen(false);
                                    setOpenSubMenu(null);
                                  }}
                                  className={({ isActive }) =>
                                    `block px-6 py-3 text-gray-700 hover:bg-gray-100 ${isActive ? "bg-gray-100 font-medium" : ""}`
                                  }
                                >
                                  <div className="flex gap-4 items-center">
                                    {subItem.icon && (
                                      <img src={process.env.REACT_APP_IMG_URL + subItem.icon} className="w-6 h-6 object-contain" alt="" />
                                    )}
                                    <p>{subItem.name}</p>
                                  </div>
                                </NavLink>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    <NavLink
                      to={item.link}
                      onClick={() => {
                        setIsOpen(false);
                        setOpenSubMenu(null);
                      }}
                      className={({ isActive }) =>
                        `block px-6 py-4 text-gray-700 hover:bg-gray-50 border-b border-gray-100 ${isActive ? "bg-[#07A024]/10 text-[#07A024]" : ""}`
                      }
                    >
                      {item.name}
                    </NavLink>
                  )}
                </div>
              );
            })}

            <div className="px-3 md:hidden">
              <ZakatButton link={"/zakat"} isFull={true} marginTop="mt-3" />
            </div>
          </div>
        </div>

      </nav>
    </div>
  );
}
